/**
 * VideoTextureUtils - Reusable video texture utilities
 * 
 * Provides shared functionality for creating and applying video textures to meshes.
 * Video textures are configured via material properties (hm_materialVideo_*).
 * 
 * Used by:
 * - Material property system (materials with hm_materialVideo_* properties)
 * - CombatSystem (for projectiles with videoMaterial configuration)
 * - CharacterLoader (for character submeshes with video materials)
 */

import { Color3, VideoTexture, type AbstractMesh, type Material, type Scene, Constants } from '@babylonjs/core';
import { logger } from '../core/Logger';

/**
 * Video material configuration parameters
 */
export interface VideoMaterialConfig {
  /** Video file path relative to public/ */
  src: string;
  /** Auto-play on spawn (default: true) */
  autoplay?: boolean;
  /** Loop video playback (default: true) */
  loop?: boolean;
  /** Emissive intensity multiplier (default: 1.0) */
  emissiveIntensity?: number;
  /** Blend mode: 'normal' or 'additive' (for FX on black backgrounds) */
  blendMode?: 'normal' | 'additive';
  /** Playback speed multiplier (default: 1.0). Values != 1.0 create separate video instances. */
  playbackRate?: number;
  /** 
   * Video loop range (normalized 0-1). Requires per-instance texture.
   * Example: { start: 0.2, end: 0.8 } loops the middle 60% of the video.
   */
  videoLoopRange?: { start: number; end: number };
}

// Track video textures per scene for reuse and cleanup
const videoTextureCache = new WeakMap<Scene, Map<string, VideoTexture>>();

/**
 * Get or create a cached video texture for a scene.
 * Textures are shared across meshes using the same video source.
 */
export function getOrCreateVideoTexture(
  scene: Scene,
  src: string,
  autoplay: boolean = true,
  loop: boolean = true
): VideoTexture {
  let cache = videoTextureCache.get(scene);
  if (!cache) {
    cache = new Map();
    videoTextureCache.set(scene, cache);
  }
  
  const existing = cache.get(src);
  if (existing) {
    return existing;
  }
  
  const texture = new VideoTexture(
    `hm_video_${src}`,
    src,
    scene,
    false, // generateMipMaps
    false, // invertY
    VideoTexture.TRILINEAR_SAMPLINGMODE,
    {
      autoPlay: autoplay,
      loop,
      autoUpdateTexture: true,
    }
  );
  
  // Flip vertically to match WebGL coordinate system
  texture.vScale = -1;
  
  // Configure video element for autoplay support
  texture.video.autoplay = autoplay;
  texture.video.loop = loop;
  texture.video.muted = true; // Required for autoplay in most browsers
  
  cache.set(src, texture);
  logger.assets.debug(`VideoTextureUtils: Created video texture for "${src}"`);
  return texture;
}

/**
 * Create a new video texture instance (not cached).
 * Used when per-instance settings like playbackRate or loopRange are needed.
 * The browser still caches the video data, but each instance has its own playback state.
 */
export function createVideoTextureInstance(
  scene: Scene,
  src: string,
  autoplay: boolean = true,
  loop: boolean = true,
  playbackRate: number = 1.0,
  loopRange?: { start: number; end: number }
): VideoTexture {
  const texture = new VideoTexture(
    `hm_video_instance_${src}_${Date.now()}`,
    src,
    scene,
    false, // generateMipMaps
    false, // invertY
    VideoTexture.TRILINEAR_SAMPLINGMODE,
    {
      autoPlay: autoplay,
      loop: false, // We'll handle looping manually if loopRange is specified
      autoUpdateTexture: true,
    }
  );
  
  // Flip vertically to match WebGL coordinate system
  texture.vScale = -1;
  
  // Configure video element
  texture.video.autoplay = autoplay;
  texture.video.muted = true;
  texture.video.playbackRate = playbackRate;
  
  // Set up custom loop range if specified
  if (loopRange) {
    const video = texture.video;
    
    // When video loads, set initial position and handle looping
    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration;
      const startTime = loopRange.start * duration;
      const endTime = loopRange.end * duration;
      
      video.currentTime = startTime;
      
      // Handle loop range by monitoring timeupdate
      const handleTimeUpdate = () => {
        if (video.currentTime >= endTime) {
          video.currentTime = startTime;
        }
      };
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      // Store cleanup handler for disposal
      (texture as any)._loopRangeCleanup = () => {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    });
    
    video.loop = false; // Disable native loop
    logger.assets.debug(`VideoTextureUtils: Created video texture with loop range ${loopRange.start}-${loopRange.end} for "${src}"`);
  } else {
    texture.video.loop = loop;
    logger.assets.debug(`VideoTextureUtils: Created video texture instance for "${src}" (rate=${playbackRate})`);
  }
  
  return texture;
}

/**
 * Apply a video texture to a material's emissive channel.
 * Works with PBR and standard materials.
 * 
 * @param blendMode - 'normal' for standard rendering, 'additive' for FX on black backgrounds
 */
export function applyVideoToMaterial(
  material: Material,
  videoTexture: VideoTexture,
  emissiveIntensity: number = 1.0,
  blendMode: 'normal' | 'additive' = 'normal'
): void {
  const mat = material as any;
  
  // Apply additive blending for FX on black backgrounds
  if (blendMode === 'additive') {
    // Enable alpha blending with additive mode (black becomes transparent)
    mat.alphaMode = Constants.ALPHA_ADD;
    mat.alpha = 1.0;
    mat.transparencyMode = 2; // ALPHABLEND
    
    // For proper additive blending, disable depth write
    mat.disableDepthWrite = true;
    
    // Ensure backface culling is off for billboards
    mat.backFaceCulling = false;
    
    // Make material unlit - critical for additive blending!
    // Without this, lighting affects the colors before blending
    if ('unlit' in mat) {
      mat.unlit = true;
    }
    
    logger.assets.debug(`VideoTextureUtils: Applied additive blending to material ${material.name}`);
  }
  
  // For PBR materials, use emissive channel for self-lit video
  if ('emissiveTexture' in mat && 'emissiveColor' in mat) {
    mat.emissiveTexture = videoTexture;
    mat.emissiveColor = new Color3(
      emissiveIntensity,
      emissiveIntensity,
      emissiveIntensity
    );
    
    // Clear albedo to avoid double display
    if ('albedoTexture' in mat) {
      mat.albedoTexture = null;
    }
    if ('albedoColor' in mat) {
      mat.albedoColor = new Color3(0, 0, 0);
    }
    
    // Ensure no metallic/roughness interference
    if ('metallic' in mat) {
      mat.metallic = 0;
    }
    if ('roughness' in mat) {
      mat.roughness = 1;
    }
  } else if ('unlit' in mat) {
    // Fallback to unlit mode for non-PBR materials
    mat.unlit = true;
    if ('diffuseTexture' in mat) {
      mat.diffuseTexture = videoTexture;
    }
  } else if ('diffuseTexture' in mat) {
    // StandardMaterial fallback
    mat.diffuseTexture = videoTexture;
    if ('emissiveTexture' in mat) {
      mat.emissiveTexture = videoTexture;
    }
    if ('emissiveColor' in mat) {
      mat.emissiveColor = new Color3(
        emissiveIntensity,
        emissiveIntensity,
        emissiveIntensity
      );
    }
  }
}

/**
 * Apply video material configuration to a mesh and all its children.
 * Clones materials before modifying to avoid affecting shared/template materials.
 * Returns the VideoTexture for later control (play/pause/etc).
 */
export function applyVideoMaterialToMesh(
  mesh: AbstractMesh,
  config: VideoMaterialConfig
): VideoTexture | null {
  if (!config.src) {
    logger.assets.warn(`VideoTextureUtils: No video src specified for mesh ${mesh.name}`);
    return null;
  }
  
  const scene = mesh.getScene();
  const autoplay = config.autoplay ?? true;
  const loop = config.loop ?? true;
  const emissiveIntensity = config.emissiveIntensity ?? 1.0;
  const blendMode = config.blendMode ?? 'normal';
  const playbackRate = config.playbackRate ?? 1.0;
  const videoLoopRange = config.videoLoopRange;
  
  // Use per-instance texture if playbackRate differs from default, or if loopRange is specified
  // (each needs its own video element for independent playback state)
  const needsPerInstance = playbackRate !== 1.0 || videoLoopRange !== undefined;
  const videoTexture = needsPerInstance
    ? createVideoTextureInstance(scene, config.src, autoplay, loop, playbackRate, videoLoopRange)
    : getOrCreateVideoTexture(scene, config.src, autoplay, loop);
  
  let appliedCount = 0;
  
  // Helper to clone material and apply video
  const applyToMesh = (targetMesh: AbstractMesh) => {
    if (targetMesh.material) {
      // Clone the material to avoid modifying shared/template materials
      const clonedMaterial = targetMesh.material.clone(`${targetMesh.material.name}_video`);
      if (clonedMaterial) {
        targetMesh.material = clonedMaterial;
        applyVideoToMaterial(clonedMaterial, videoTexture, emissiveIntensity, blendMode);
        appliedCount++;
        logger.assets.debug(`VideoTextureUtils: Applied video to ${targetMesh.name} (material: ${clonedMaterial.name}, blendMode: ${blendMode}, rate: ${playbackRate})`);
      }
    }
  };
  
  // Apply to root mesh material (if it has one)
  applyToMesh(mesh);
  
  // Also apply to any child meshes
  mesh.getChildMeshes().forEach((child) => {
    applyToMesh(child);
  });
  
  // Store reference in metadata for cleanup (and whether it's a per-instance texture)
  if (!mesh.metadata) mesh.metadata = {};
  mesh.metadata.hmVideoTexture = videoTexture;
  mesh.metadata.hmVideoTextureOwned = playbackRate !== 1.0; // Track if we own this texture (need to dispose)
  
  if (appliedCount === 0) {
    logger.assets.warn(`VideoTextureUtils: No materials found on mesh ${mesh.name} or its ${mesh.getChildMeshes().length} children`);
  } else {
    logger.assets.debug(`VideoTextureUtils: Applied video "${config.src}" to ${appliedCount} meshes under ${mesh.name}`);
  }
  
  return videoTexture;
}

/**
 * Start video playback on a mesh that has a video texture applied.
 */
export function playVideoOnMesh(mesh: AbstractMesh): void {
  const videoTexture = mesh.metadata?.hmVideoTexture as VideoTexture | undefined;
  if (!videoTexture) {
    logger.assets.warn(`VideoTextureUtils: No video texture found on mesh ${mesh.name}`);
    return;
  }
  
  videoTexture.video.play().catch((error) => {
    // Autoplay blocking is a browser policy, not a loading error.
    // User interaction will enable playback. Warn but don't throw.
    logger.assets.warn(`VideoTextureUtils: Autoplay blocked for ${mesh.name}: ${error.message}`);
  });
}

/**
 * Pause video playback on a mesh that has a video texture applied.
 */
export function pauseVideoOnMesh(mesh: AbstractMesh): void {
  const videoTexture = mesh.metadata?.hmVideoTexture as VideoTexture | undefined;
  if (!videoTexture) return;
  
  videoTexture.video.pause();
}

/**
 * Clean up video texture reference from a mesh.
 * Note: Does not dispose the texture as it may be shared.
 */
export function cleanupVideoFromMesh(mesh: AbstractMesh): void {
  if (!mesh.metadata?.hmVideoTexture) return;
  delete mesh.metadata.hmVideoTexture;
}

/**
 * Extract video material config from extras (from Blender hm_materialVideo_* properties).
 * This is a MATERIAL-LEVEL property, not an entity behavior.
 * Any mesh can have a video texture regardless of entity type.
 * 
 * Properties checked:
 * - hm_materialVideo_enabled: boolean
 * - hm_materialVideo_src: string (path relative to public/)
 * - hm_materialVideo_loop: boolean (default: true)
 * - hm_materialVideo_autoplay: boolean (default: true)
 * - hm_materialVideo_emissiveIntensity: number (default: 1.0)
 * 
 * Returns null if no video material is configured.
 */
export function extractVideoConfigFromExtras(extras: Record<string, any> | null | undefined): VideoMaterialConfig | null {
  if (!extras) return null;
  
  // Check for hm_materialVideo_* properties (the correct approach)
  // Note: Blender exports booleans as 0/1, so check for truthy, not === true
  const enabled = extras.hm_materialVideo_enabled;
  const src = extras.hm_materialVideo_src;
  
  logger.assets.debug(`extractVideoConfigFromExtras: enabled=${enabled} (type: ${typeof enabled}), src=${src}`);
  
  if (enabled && src) {
    return {
      src: src,
      autoplay: extras.hm_materialVideo_autoplay !== false && extras.hm_materialVideo_autoplay !== 0,
      loop: extras.hm_materialVideo_loop !== false && extras.hm_materialVideo_loop !== 0,
      emissiveIntensity: extras.hm_materialVideo_emissiveIntensity ?? 1.0,
      blendMode: extras.hm_materialVideo_blendMode === 'additive' ? 'additive' : 'normal',
      playbackRate: extras.hm_materialVideo_playbackRate ?? 1.0,
    };
  }
  
  return null;
}

/**
 * Extract video config from a mesh - checks both mesh extras AND material extras.
 * Video textures are material properties, so we check both locations.
 */
export function extractVideoConfigFromMesh(mesh: AbstractMesh): VideoMaterialConfig | null {
  // First check mesh extras (Object-level custom properties in Blender)
  const meshExtras = mesh.metadata?.gltf?.extras;
  if (meshExtras) {
    logger.assets.debug(`extractVideoConfigFromMesh: mesh "${mesh.name}" has extras:`, Object.keys(meshExtras));
    const meshConfig = extractVideoConfigFromExtras(meshExtras);
    if (meshConfig) {
      logger.assets.debug(`  -> Found video config on mesh extras: ${meshConfig.src}`);
      return meshConfig;
    }
  }
  
  // Then check material extras (Material-level custom properties in Blender)
  if (mesh.material) {
    const matExtras = (mesh.material as any).metadata?.gltf?.extras;
    if (matExtras) {
      logger.assets.debug(`extractVideoConfigFromMesh: mesh "${mesh.name}" material "${mesh.material.name}" has extras:`, Object.keys(matExtras));
      const matConfig = extractVideoConfigFromExtras(matExtras);
      if (matConfig) {
        logger.assets.debug(`  -> Found video config on material extras: ${matConfig.src}`);
        return matConfig;
      }
    }
  }
  
  return null;
}

